import React, { useState } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip
} from 'recharts';

const styles: Record<string, React.CSSProperties> = {
    container: {
        padding: '16px',
        maxWidth: '100vw',
        overflowX: 'hidden',
        color: 'var(--text-main)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    introTitle: {
        fontSize: '20px',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: 'var(--text-heading)',
        margin: 0
    },
    introDesc: {
        color: 'var(--text-muted)',
        fontSize: '13px',
        lineHeight: '1.5',
        margin: 0
    },
    selectorContainer: {
        display: 'flex',
        gap: '8px',
        overflowX: 'auto',
        paddingBottom: '8px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
    },
    strategyTabButton: {
        padding: '10px 16px',
        borderRadius: '24px',
        border: '1px solid var(--border-color)',
        backgroundColor: 'var(--bg-box)',
        color: 'var(--text-main)',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease'
    },
    activeStrategyTabButton: {
        backgroundColor: '#7c3aed',
        borderColor: '#7c3aed',
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)'
    },
    kpiCard: {
        background: 'var(--bg-box)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative'
    },
    kpiTitle: {
        fontSize: '16px',
        fontWeight: 800,
        color: 'var(--text-heading)',
        marginBottom: '6px'
    },
    kpiDesc: {
        fontSize: '12px',
        color: 'var(--text-muted)',
        marginBottom: '16px',
        lineHeight: '1.4'
    },
    kpiRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        width: '100%',
        marginTop: '8px'
    },
    kpiBox: {
        backgroundColor: 'var(--bg-main)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '12px 8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    kpiLabel: {
        fontSize: '9px',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        fontWeight: 700,
        marginBottom: '4px'
    },
    kpiValue: {
        fontSize: '18px',
        fontWeight: 800
    },
    chartHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: 700,
        color: 'var(--text-heading)'
    },
    chartBox: {
        background: 'var(--bg-box)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    sectionTitle: {
        fontSize: '15px',
        fontWeight: 700,
        color: 'var(--text-heading)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    badge: {
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase'
    },
    stockCardsContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%'
    },
    stockCard: {
        backgroundColor: 'var(--bg-box)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    stockHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
    },
    symbolText: {
        fontSize: '16px',
        fontWeight: 800,
        color: '#7c3aed',
        textDecoration: 'none'
    },
    stockDetailsGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px',
        backgroundColor: 'var(--bg-main)',
        padding: '10px',
        borderRadius: '8px',
        fontSize: '12px'
    },
    stockDetailsItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px'
    },
    stockDetailsLabel: {
        fontSize: '10px',
        color: 'var(--text-muted)'
    },
    stockDetailsValue: {
        fontWeight: 600,
        color: 'var(--text-main)'
    },
    stockReasonText: {
        fontSize: '12px',
        lineHeight: '1.4',
        color: 'var(--text-muted)',
        margin: 0
    },
    timelineContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '8px 0'
    },
    timelineCard: {
        backgroundColor: 'var(--bg-box)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        position: 'relative'
    },
    timelineHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    timelineTitle: {
        fontSize: '13px',
        fontWeight: 700
    },
    timelineDate: {
        fontSize: '11px',
        color: 'var(--text-muted)'
    },
    timelineCollapseButton: {
        border: 'none',
        background: 'none',
        color: '#3b82f6',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'pointer',
        padding: 0,
        textAlign: 'left'
    }
};

const CustomChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const p1 = payload[0];
    const p2 = payload[1];
    
    return (
        <div style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: 12,
            padding: '10px 14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            fontSize: 12,
            minWidth: 160,
            backdropFilter: 'blur(8px)'
        }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text-heading)' }}>
                {label ? new Date(label).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }) : '—'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#7c3aed' }}></span>
                    IA
                </span>
                <span style={{ fontWeight: 700, color: '#a78bfa' }}>{p1?.value !== null && p1?.value !== undefined ? `${p1.value >= 0 ? '+' : ''}${p1.value.toFixed(1)}` : '0.0'}%</span>
            </div>
            {p2 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#64748b' }}></span>
                        Index
                    </span>
                    <span style={{ fontWeight: 700, color: '#94a3b8' }}>{p2?.value !== null && p2?.value !== undefined ? `${p2.value >= 0 ? '+' : ''}${p2.value.toFixed(1)}` : '0.0'}%</span>
                </div>
            )}
        </div>
    );
};

interface MobileAiWatchlistProps {
    strategies: any[];
    selectedId: number | null;
    setSelectedId: (id: number) => void;
    details: any;
    history: any[];
    loading: boolean;
    detailsLoading: boolean;
    isPro: boolean;
    handleUpgrade: () => void;
    formatDate: (dateString: string) => string;
    getRiskLabelStyle: (profile: string) => React.CSSProperties;
}

const MobileAiWatchlist: React.FC<MobileAiWatchlistProps> = ({
    strategies,
    selectedId,
    setSelectedId,
    details,
    history,
    loading,
    detailsLoading,
    isPro,
    handleUpgrade,
    formatDate,
    getRiskLabelStyle
}) => {
    const [expandedHistory, setExpandedHistory] = useState<Record<number, boolean>>({});

    const toggleHistoryExpand = (idx: number) => {
        setExpandedHistory(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    const activeStrategy = strategies.find(s => s.id === selectedId);

    return (
        <div style={styles.container}>
            {/* Intro */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <h2 style={styles.introTitle}>Watchlists IA ⚡</h2>
                <p style={styles.introDesc}>
                    Portefeuilles thématiques BRVM optimisés mensuellement par nos modèles quantitatifs.
                </p>
            </div>

            {/* Strategy scroll selector */}
            <div style={styles.selectorContainer}>
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} style={{ ...styles.strategyTabButton, width: '120px', height: '36px', opacity: 0.5 }} />
                    ))
                ) : (
                    strategies.map(s => {
                        const isSelected = selectedId === s.id;
                        return (
                            <button
                                key={s.id}
                                onClick={() => setSelectedId(s.id)}
                                style={{
                                    ...styles.strategyTabButton,
                                    ...(isSelected ? styles.activeStrategyTabButton : {})
                                }}
                            >
                                {s.name}
                            </button>
                        );
                    })
                )}
            </div>

            {/* active strategy detail summary */}
            {activeStrategy && (
                <div style={styles.kpiCard}>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                        <span style={getRiskLabelStyle(activeStrategy.risk_profile)}>
                            {activeStrategy.risk_profile === 'HIGH' ? 'Risque Élevé' : activeStrategy.risk_profile === 'MEDIUM' ? 'Risque Moyen' : 'Risque Modéré'}
                        </span>
                    </div>
                    
                    <h3 style={styles.kpiTitle}>{activeStrategy.name}</h3>
                    <p style={styles.kpiDesc}>{activeStrategy.description}</p>
                    
                    <div style={styles.kpiRow}>
                        <div style={styles.kpiBox}>
                            <span style={styles.kpiLabel}>Performance Cumulée</span>
                            <span style={{ ...styles.kpiValue, color: (activeStrategy.performance_cumulated ?? 0) >= 0 ? '#10b981' : '#ef4444' }}>
                                {(activeStrategy.performance_cumulated ?? 0) >= 0 ? '+' : ''}{(activeStrategy.performance_cumulated ?? 0).toFixed(1)}%
                            </span>
                        </div>
                        <div style={styles.kpiBox}>
                            <span style={styles.kpiLabel}>Benchmark (BRVM)</span>
                            <span style={{ ...styles.kpiValue, color: 'var(--text-muted)' }}>
                                {(activeStrategy.benchmark_performance_cumulated ?? 0) >= 0 ? '+' : ''}{(activeStrategy.benchmark_performance_cumulated ?? 0).toFixed(1)}%
                            </span>
                        </div>
                    </div>

                    {activeStrategy.strategy_type === 'YIELD' && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <i className="material-icons" style={{ fontSize: '12px' }}>payments</i>
                            Hors dividendes perçus (Total Return supérieur)
                        </div>
                    )}

                    {/* Échéance passée sans révision : ne jamais présenter
                        une date échue comme « prochaine révision ». */}
                    {activeStrategy.revision_en_retard ? (
                        <div style={{ marginTop: '12px', fontSize: '10px', color: '#d97706', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <i className="material-icons" style={{ fontSize: '12px' }}>update</i>
                            Révision du {formatDate(activeStrategy.next_revision_date)} reportée · sélection maintenue
                        </div>
                    ) : activeStrategy.next_revision_date && (
                        <div style={{ marginTop: '12px', fontSize: '10px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <i className="material-icons" style={{ fontSize: '12px' }}>schedule</i>
                            Prochaine révision : {formatDate(activeStrategy.next_revision_date)}
                        </div>
                    )}
                </div>
            )}

            {/* History Performance Chart */}
            {detailsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '180px', color: 'var(--text-muted)', gap: '8px' }}>
                    <i className="material-icons spin" style={{ fontSize: '20px' }}>hourglass_empty</i>
                    <span style={{ fontSize: '13px' }}>Chargement de l'historique...</span>
                </div>
            ) : details && (
                <div style={styles.chartBox}>
                    <div style={styles.chartHeader}>
                        <i className="material-icons" style={{ color: '#7c3aed', fontSize: '18px' }}>show_chart</i>
                        Performance vs BRVM Composite
                    </div>
                    
                    <div style={{ width: '100%', height: '220px', marginTop: '8px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={details.performance_history || []} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="ai-gradient-mobile" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.25} />
                                        <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.01} />
                                    </linearGradient>
                                    <linearGradient id="bench-gradient-mobile" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#64748b" stopOpacity={0.12} />
                                        <stop offset="95%" stopColor="#64748b" stopOpacity={0.01} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" opacity={0.25} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={d => new Date(d).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })}
                                    tick={{ fill: 'var(--text-muted)', fontSize: 9 }}
                                    axisLine={false}
                                    tickLine={false}
                                    minTickGap={25}
                                />
                                <YAxis
                                    tickFormatter={v => `${v >= 0 ? '+' : ''}${v}%`}
                                    tick={{ fill: 'var(--text-muted)', fontSize: 9 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip content={<CustomChartTooltip />} />
                                <Area
                                    name="Stratégie"
                                    type="monotone"
                                    dataKey="performance_pct"
                                    stroke="#7c3aed"
                                    strokeWidth={2}
                                    fill="url(#ai-gradient-mobile)"
                                />
                                <Area
                                    name="BRVM"
                                    type="monotone"
                                    dataKey="benchmark_performance_pct"
                                    stroke="#64748b"
                                    strokeWidth={1.5}
                                    fill="url(#bench-gradient-mobile)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Current Stock Selections */}
            {!detailsLoading && details && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={styles.sectionHeader}>
                        <div style={styles.sectionTitle}>
                            <i className="material-icons" style={{ color: '#7c3aed', fontSize: '18px' }}>playlist_add_check</i>
                            Composition du Mois Courant
                        </div>
                        {details.strategy?.last_revision_date && (
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                Révision IA : {formatDate(details.strategy.last_revision_date)}
                            </span>
                        )}
                    </div>

                    <div style={styles.stockCardsContainer}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {details.stocks?.map((s: any) => {
                                const gainPct: number | null = s.gain_pct ?? null;
                                const gainColor = gainPct === null ? 'var(--text-muted)'
                                    : gainPct >= 0 ? '#10b981' : '#ef4444';

                                return (
                                    <div key={s.id} style={styles.stockCard}>
                                        <div style={styles.stockHeader}>
                                            <div>
                                                <span style={styles.symbolText}>
                                                    {s.stock_symbol}
                                                </span>
                                                <div style={{ marginTop: '4px' }}>
                                                    <span style={{
                                                        padding: '3px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '10px',
                                                        fontWeight: 600,
                                                        background: 'rgba(124, 58, 237, 0.1)',
                                                        color: '#a78bfa'
                                                    }}>
                                                        {s.reason_category}
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>P&L</div>
                                                <span style={{ fontSize: '14px', fontWeight: 800, color: gainColor }}>
                                                    {isPro ? (gainPct !== null ? `${gainPct >= 0 ? '+' : ''}${gainPct.toFixed(2)}%` : '—') : '••%'}
                                                </span>
                                            </div>
                                        </div>

                                        <div style={styles.stockDetailsGrid}>
                                            <div style={styles.stockDetailsItem}>
                                                <span style={styles.stockDetailsLabel}>Date d'Ajout</span>
                                                <span style={styles.stockDetailsValue}>{formatDate(s.added_date)}</span>
                                            </div>
                                            <div style={styles.stockDetailsItem}>
                                                <span style={styles.stockDetailsLabel}>Prix d'Entrée</span>
                                                <span style={styles.stockDetailsValue}>
                                                    {isPro ? `${s.added_price?.toLocaleString() ?? '0'} XOF` : '•••• XOF'}
                                                </span>
                                            </div>
                                        </div>

                                        <div>
                                            <p style={styles.stockReasonText}>
                                                {isPro ? s.reasoning : "Abonnez-vous à Kanea Pro pour débloquer le raisonnement financier détaillé."}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Paywall Overlay on mobile cards list */}
                        {!isPro && (
                            <div className="paywall-overlay" style={{ borderRadius: '12px' }}>
                                <div className="paywall-icon-circle" style={{ width: '48px', height: '48px', marginBottom: '8px' }}>
                                    <i className="material-icons" style={{ fontSize: '22px', color: '#7c3aed' }}>lock</i>
                                </div>
                                <h4 className="paywall-title" style={{ fontSize: '14px', marginBottom: '4px' }}>
                                    Composition Verrouillée
                                </h4>
                                <p className="paywall-desc" style={{ fontSize: '12px', maxWidth: '280px', marginBottom: '12px' }}>
                                    Accédez aux actions de la watchlist, aux prix d'entrée identifiés et au raisonnement de l'algorithme.
                                </p>
                                <button onClick={handleUpgrade} className="paywall-btn" style={{ padding: '8px 16px', fontSize: '12px' }}>
                                    Débloquer maintenant
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Timeline of moves */}
            {details && history.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                    <div style={styles.sectionTitle}>
                        <i className="material-icons" style={{ color: '#7c3aed', fontSize: '18px' }}>history</i>
                        Historique des Mouvements
                    </div>

                    <div style={styles.timelineContainer}>
                        {(isPro ? history : history.slice(0, 3)).map((m: any, idx: number) => {
                            const isAdded = m.status === 'ACTIVE';
                            const isExpanded = !!expandedHistory[idx];
                            const text = m.reasoning || '';
                            const isLong = text.length > 100;

                            return (
                                <div key={m.id} style={styles.timelineCard}>
                                    <div style={styles.timelineHeader}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                color: isAdded ? '#10b981' : '#ef4444',
                                                backgroundColor: isAdded ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                                padding: '2px 6px',
                                                borderRadius: '4px'
                                            }}>
                                                {isAdded ? 'ENTRÉE' : 'SORTIE'}
                                            </span>
                                            <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-heading)' }}>
                                                {isPro ? m.stock_symbol : 'LOCK'}
                                            </span>
                                        </div>
                                        <span style={styles.timelineDate}>
                                            {isAdded ? formatDate(m.added_date) : formatDate(m.removed_date || m.added_date)}
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)' }}>
                                        <span>Entrée : {isPro ? `${m.added_price?.toLocaleString()} XOF` : '••••'}</span>
                                        {!isAdded && m.removed_price && (
                                            <span>Sortie : {isPro ? `${m.removed_price?.toLocaleString()} XOF` : '••••'}</span>
                                        )}
                                        {m.gain_pct !== null && m.gain_pct !== undefined && isPro && (
                                            <span style={{ fontWeight: 700, color: m.gain_pct >= 0 ? '#10b981' : '#ef4444' }}>
                                                {m.gain_pct >= 0 ? '+' : ''}{m.gain_pct.toFixed(1)}%
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <p style={{ ...styles.stockReasonText, margin: 0 }}>
                                            {isExpanded ? text : (isLong ? `${text.slice(0, 100)}...` : text)}
                                        </p>
                                        {isLong && (
                                            <button
                                                onClick={() => toggleHistoryExpand(idx)}
                                                style={{ ...styles.timelineCollapseButton, marginTop: '4px' }}
                                            >
                                                {isExpanded ? '▲ Masquer' : '▼ Lire la suite'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Teaser verrouillé pour non-pro */}
                        {!isPro && history.length > 3 && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 14px',
                                borderRadius: '10px',
                                border: '1px dashed rgba(124, 58, 237, 0.4)',
                                background: 'rgba(124, 58, 237, 0.06)',
                                marginTop: '4px'
                            }}>
                                <i className="material-icons" style={{ color: '#7c3aed', fontSize: '18px' }}>lock</i>
                                <div style={{ flex: 1 }}>
                                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-heading)' }}>
                                        {history.length - 3} autres mouvements verrouillés
                                    </span>
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                        Historique complet avec Kanea Pro.
                                    </div>
                                </div>
                                <button onClick={handleUpgrade} style={{
                                    padding: '6px 12px',
                                    borderRadius: '8px',
                                    background: '#7c3aed',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                }}>
                                    Pro ⚡
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileAiWatchlist;
